import { pathsV2Creators } from 'core/services/navigation/usePathsV2';
import {
  CandidateProfileListProps,
  CandidateProfileListType
} from 'modules/candidate-profile/types/CandidateProfileListProps';

type GetCandidateProfileLinkParams = {
  candidateProfileListProps: CandidateProfileListProps;
  id: string;
  searchParams: URLSearchParams;
};

export const getCandidateProfileLink = ({
  candidateProfileListProps,
  id,
  searchParams
}: GetCandidateProfileLinkParams): string => {
  switch (candidateProfileListProps.type) {
    case CandidateProfileListType.CANDIDATES_LIST:
      return pathsV2Creators.CANDIDATE_PROFILE_DETAILS({
        candidateProfileId: id,
        searchParams: Object.fromEntries(searchParams)
      });
    case CandidateProfileListType.CANDIDATES_FROM_TALENT_POOL:
      return pathsV2Creators.TALENT_POOL_CANDIDATE_PROFILE_DETAILS({
        candidateProfileId: id,
        talentPoolId: candidateProfileListProps.talentPoolId,
        searchParams: Object.fromEntries(searchParams)
      });
    case CandidateProfileListType.ADD_CANDIDATES_TO_TALENT_POOL:
      return pathsV2Creators.TALENT_POOL_ADD_CANDIDATES_CANDIDATE_PROFILE_DETAILS(
        {
          candidateProfileId: id,
          talentPoolId: candidateProfileListProps.talentPoolId,
          searchParams: Object.fromEntries(searchParams)
        }
      );
    case CandidateProfileListType.ANALYSIS_CANDIDATES_LIST:
      return pathsV2Creators.ANALYSE_CANDIDATE_PROFILE_DETAILS({
        candidateProfileId: id,
        candidateBatchId: candidateProfileListProps.candidateBatchId,
        searchParams: Object.fromEntries(searchParams)
      });
    case CandidateProfileListType.ANALYSIS_CANDIDATES_FROM_TALENT_POOL:
      return pathsV2Creators.ANALYSE_TALENT_POOL_CANDIDATE_PROFILE_DETAILS({
        candidateProfileId: id,
        candidateBatchId: candidateProfileListProps.candidateBatchId,
        talentPoolId: candidateProfileListProps.talentPoolId,
        searchParams: Object.fromEntries(searchParams)
      });
  }
};

type GetCandidateProfileLinkPropsParams = GetCandidateProfileLinkParams & {
  /**
   * The current path (pathname + search) to store as "from" for back navigation.
   * MUST include search params for scroll restoration to work correctly.
   * Use: `location.pathname + location.search`
   */
  currentPath: string;
};

type CandidateProfileLinkProps = {
  to: string;
  state: { from: string };
};

/**
 * Returns link props (to + state) for navigating to candidate profile details.
 *
 * ## Scroll Restoration Support
 *
 * The `state.from` property stores the full origin URL (path + search params).
 * This is used by `useCandidateProfileDetailNavigation.handleBack()` to navigate
 * back to the exact URL, which is required for scroll restoration to work.
 *
 * The scroll position is keyed by full URL in sessionStorage, so if we navigated
 * back without preserving search params (filters, pagination, searchQueryId),
 * the saved scroll position wouldn't be found.
 *
 * Flow:
 * 1. User clicks link -> state.from captures current URL
 * 2. User clicks back -> handleBack() navigates to state.from
 * 3. useTableScrollRestoration finds saved position by URL key
 * 4. Scroll is restored once table data loads
 *
 * @see useTableScrollRestoration - The hook that restores scroll position
 * @see useCandidateProfileDetailNavigation - The hook that handles back navigation
 */
export const getCandidateProfileLinkProps = ({
  candidateProfileListProps,
  id,
  searchParams,
  currentPath
}: GetCandidateProfileLinkPropsParams): CandidateProfileLinkProps => {
  const to = getCandidateProfileLink({
    candidateProfileListProps,
    id,
    searchParams
  });

  return {
    to,
    state: { from: currentPath }
  };
};
